/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/await-thenable, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call */
import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryReturnValue,
  TypedUseLazyQuery,
  TypedUseLazyQueryStateResult,
  TypedUseMutation,
  TypedUseMutationResult,
} from '@reduxjs/toolkit/query/react';
import { chunk } from 'es-toolkit';

import useEnhancedApi, {
  type EnhancedLazyTrigger,
  type EnhancedMutationTrigger,
} from '../hooks/use-enhanced-api';
import type { AppFetchArgs } from '../types/api-types';

type EnhancedApi<T> = {
  [K in keyof T]: T[K] extends TypedUseLazyQuery<
    infer ResultType,
    infer QueryArg,
    infer BaseQuery
  >
    ? (
        ...args: Parameters<T[K]>
      ) => [
        EnhancedLazyTrigger<ResultType, QueryArg, BaseQuery>,
        TypedUseLazyQueryStateResult<ResultType, QueryArg, BaseQuery>,
        ReturnType<T[K]>[2],
      ]
    : T[K] extends TypedUseMutation<
          infer ResultType,
          infer QueryArg,
          infer BaseQuery
        >
      ? (
          ...args: Parameters<T[K]>
        ) => [
          EnhancedMutationTrigger<ResultType, QueryArg, BaseQuery>,
          TypedUseMutationResult<ResultType, QueryArg, BaseQuery>,
        ]
      : T[K];
};

export function enhanceApi<T extends Record<string, any>>(
  api: T
): EnhancedApi<T> {
  const enhanced: any = {};

  Object.keys(api).forEach(key => {
    const val = api[key];
    if (
      key.startsWith('use') &&
      (key.endsWith('Mutation') || key.startsWith('useLazy'))
    ) {
      enhanced[key] = (...args: any[]) => useEnhancedApi(val, ...args);
    } else {
      enhanced[key] = val;
    }
  });

  return enhanced as EnhancedApi<T>;
}

export const handleQueryInBatches = async (
  rawFetchBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    object,
    FetchBaseQueryMeta
  >,
  args: AppFetchArgs & {
    meta: { batch: NonNullable<NonNullable<AppFetchArgs['meta']>['batch']> };
  },
  api: BaseQueryApi,
  extraOptions: any
): Promise<
  QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
> => {
  let promises;
  if (args.meta.batch.in === 'body') {
    const property = args.meta.batch.property;

    const batches = chunk(
      property ? args.body[property] : args.body,
      args.meta.batch.size
    );

    promises = batches.map(batch =>
      rawFetchBaseQuery(
        {
          ...args,
          body: property
            ? {
                ...args.body,
                [property]: batch,
              }
            : batch,
        },
        api,
        extraOptions
      )
    );
  } else {
    const property = args.meta.batch.property;
    const batches = chunk(
      args.params![property].split(args.meta.batch.format),
      args.meta.batch.size
    );
    promises = batches.map(batch =>
      rawFetchBaseQuery(
        {
          ...args,
          params: {
            ...args.params,
            [property]: batch.join(args.meta.batch.format),
          },
        },
        api,
        extraOptions
      )
    );
  }

  const results = await Promise.all(promises);

  if (args.meta.batch.merge) {
    return { data: args.meta.batch.merge(results) };
  }

  return {
    data: [],
  };
};
